import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ChevronRightIcon, PaperclipIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { foodTruckApplication, foodTruckDays, foodTruckDocuments, foodTruckMenuTypes, foodTruckZones } from '../data/permis';
import { usePermits } from '../contexts/PermitsContext';
type Step = 0 | 1 | 2 | 3;
const steps = [{
  id: 0,
  label: 'Demandeur'
}, {
  id: 1,
  label: 'Entreprise et véhicule'
}, {
  id: 2,
  label: 'Emplacements et horaire'
}, {
  id: 3,
  label: 'Documents et déclaration'
}] as const;
const fieldClass = 'mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus:ring-2 focus:ring-ville-indigo/20';
const labelClass = 'block text-[13px] font-bold text-ville-navy';
const requiredMark = <span className="text-ville-coral" data-id="element-3423" data-mp="uwc1gbythdan"> *</span>;
function Field({
  id,
  label,
  required,
  hint,
  children
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return <p data-id="element-3424" data-mp="ktewqiuq2t80">
      <label htmlFor={id} className={labelClass} data-id="element-3425" data-mp="62pit8pvn00g">
        {label}
        {required && requiredMark}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500" data-id="element-3426" data-mp="b2kctl4y85pq">{hint}</span>}
    </p>;
}
export function PermisFormulaire() {
  const navigate = useNavigate();
  const {
    addPermit
  } = usePermits();
  const [step, setStep] = useState<Step>(0);
  const [zones, setZones] = useState<string[]>(['cite']);
  const [days, setDays] = useState<string[]>(['Jeudi', 'Vendredi', 'Samedi', 'Dimanche']);
  const [documents, setDocuments] = useState<string[]>([]);
  const [declared, setDeclared] = useState(false);
  const [sent, setSent] = useState(false);
  const toggle = (list: string[], value: string, set: (next: string[]) => void) => set(list.includes(value) ? list.filter(item => item !== value) : [...list, value]);
  const submit = () => {
    addPermit(foodTruckApplication);
    setSent(true);
  };
  if (sent) {
    return <main className="bg-white" data-id="element-3427" data-mp="1g1h7wp6j7tzl">
        <div className="mx-auto max-w-[760px] px-5 py-16 lg:px-10" data-id="element-3428" data-mp="1b9pgkcfjxk8e">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-3429" data-mp="1b7w7yrldopxr">
            <CheckIcon className="h-7 w-7" data-id="element-3430" data-mp="11cuifun6xlun" />
          </span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ville-navy" data-id="element-3431" data-mp="xa7o9n31roua">
            Formulaire transmis à la Ville
          </h1>
          <p className="mt-2 text-[17px] text-slate-600" data-id="element-3432" data-mp="sbwcv7x3t69y">
            Votre demande de permis d’exploitation d’un véhicule de restauration porte le numéro{' '}
            {foodTruckApplication.reference}. L’analyse prend {foodTruckApplication.processingTime}. Les frais de{' '}
            {foodTruckApplication.fee} seront exigibles seulement si la demande est acceptée.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-3433" data-mp="2pac0cejmttcc">
            <Link to={`/mes-permis/${foodTruckApplication.id}`} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3434" data-mp="2u8nakqdua5v3">
              Suivre ma demande
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3435" data-mp="2b99g87c76t42" />
            </Link>
            <Link to="/mes-permis" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3436" data-mp="2g95hvzxfa8gg">
              Retour à mes permis
            </Link>
          </div>
        </div>
      </main>;
  }
  return <main className="bg-white" data-id="element-3437" data-mp="3uaa9zx1fwm8o">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-3438" data-mp="3q7nftphaqp8b">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-3439" data-mp="8193tw2l8pck">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3440" data-mp="31mq7liul738">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3441" data-mp="h0vx1ul5youj" />
          <Link to="/mes-permis" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3442" data-mp="d0ls8l93y4h9">
            Mes permis
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3443" data-mp="qqfh33km4oqz" />
          <span aria-current="page" data-id="element-3444" data-mp="ls5l4qr2dk4b">Formulaire</span>
        </nav>

        <header className="mt-4" data-id="element-3445" data-mp="lv040dufiwvm">
          <p className="text-sm font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-3446" data-mp="10p4ykv8lk76p">
            Formulaire U-214 · Urbanisme et permis
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-ville-navy lg:text-4xl" data-id="element-3447" data-mp="voqqxeawqz97">
            Permis d’exploitation d’un véhicule de restauration
          </h1>
          <p className="mt-2 max-w-2xl text-[17px] text-slate-600" data-id="element-3448" data-mp="1jdoxtx54spor">
            Commerce mobile sur le domaine public. Frais de {foodTruckApplication.fee}, payables uniquement si
            la demande est acceptée. Délai d’analyse : {foodTruckApplication.processingTime}.
          </p>
          <p className="mt-3 flex items-start gap-2 rounded-2xl bg-ville-indigo-soft px-4 py-3 text-sm text-ville-navy" data-id="element-3449" data-mp="vnuldgoq5351">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-3450" data-mp="10gnra7vxihip" />
            ATNA peut remplir ce formulaire pour vous en quelques questions.{' '}
            <Link to="/mes-permis" className="font-bold text-ville-indigo hover:underline" data-id="element-3451" data-mp="rnkd1xdgm0n0">
              Utiliser ATNA
            </Link>
          </p>
        </header>

        <ol className="mt-8 flex flex-wrap items-center gap-2 text-xs font-bold" data-id="element-3452" data-mp="hp4ljtaispcz">
          {steps.map((item, index) => {
          const done = step > index;
          const active = step === index;
          return <li key={item.id} className="flex items-center gap-2" data-id="element-3453" data-mp="3pxcma23ams6m" data-mpi={index}>
                <span className={`rounded-full px-3 py-1.5 ${active ? 'bg-ville-navy text-white' : done ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-500'}`} data-id="element-3454" data-mp="3kz1ctsgwujox">
                  {index + 1}. {item.label}
                </span>
                {index < steps.length - 1 && <span className="h-px w-4 bg-slate-200" aria-hidden="true" data-id="element-3455" data-mp="7j3cpeqdjxdf" />}
              </li>;
        })}
        </ol>

        <form className="mt-8 rounded-3xl border border-slate-200 p-6 sm:p-8" onSubmit={event => {
        event.preventDefault();
        if (step < 3) {
          setStep(current => current + 1 as Step);
          return;
        }
        if (declared) submit();
      }} data-id="element-3456" data-mp="2sb5iuviq75vu">
          {step === 0 && <fieldset data-id="element-3457" data-mp="17te6bnhfkhsv">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3458" data-mp="17qrwtiazbs80">
                Section 1 — Identification du demandeur
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3459" data-mp="1hkiikffq1ok0">Les champs marqués d’un astérisque sont obligatoires.</p>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3460" data-mp="1mk3k8xma0b0f">
                <Field id="nom" label="Nom" required data-id="element-3461" data-mp="1qikuvr4wo3ml">
                  <input id="nom" defaultValue="Bouchard" className={fieldClass} data-id="element-3462" data-mp="1vipa7du2usb8" />
                </Field>
                <Field id="prenom" label="Prénom" required data-id="element-3463" data-mp="20hs7ud7q9w96">
                  <input id="prenom" defaultValue="Charlotte" className={fieldClass} data-id="element-3464" data-mp="20h0l7rw3z8v1" />
                </Field>
                <Field id="courriel" label="Courriel" required data-id="element-3465" data-mp="16sfnc0c96by">
                  <input id="courriel" type="email" defaultValue="charlotte.bouchard@courriel.com" className={fieldClass} data-id="element-3466" data-mp="64u0a1dexdpj" />
                </Field>
                <Field id="telephone" label="Téléphone" required data-id="element-3467" data-mp="230do9f8g341d">
                  <input id="telephone" type="tel" defaultValue="450 555-0184" className={fieldClass} data-id="element-3468" data-mp="2hqkzb9w4hks1" />
                </Field>
                <Field id="adresse" label="Adresse du demandeur" required data-id="element-3469" data-mp="2cudq988fjt5v">
                  <input id="adresse" defaultValue="4590, rue Bourgogne" className={fieldClass} data-id="element-3470" data-mp="1p2ytduzfa420" />
                </Field>
                <Field id="postal" label="Code postal" required data-id="element-3471" data-mp="1k5ghpwu0taqb">
                  <input id="postal" defaultValue="J4W 2K1" className={fieldClass} data-id="element-3472" data-mp="1z1eb57ck80yu" />
                </Field>
                <Field id="qualite" label="À quel titre faites-vous la demande ?" required data-id="element-3473" data-mp="1tzys1kty04j3">
                  <select id="qualite" defaultValue="proprietaire" className={fieldClass} data-id="element-3474" data-mp="3aegw0elozcot">
                    <option value="proprietaire" data-id="element-3475" data-mp="35fx47xkf36oj">Propriétaire de l’entreprise</option>
                    <option value="mandataire" data-id="element-3476" data-mp="3czy80yasax2a">Mandataire</option>
                    <option value="organisme" data-id="element-3477" data-mp="3hzaxzrwyck93">Organisme à but non lucratif</option>
                  </select>
                </Field>
                <Field id="residence" label="Résident de Brossard" required data-id="element-3478" data-mp="340b5282epj74">
                  <select id="residence" defaultValue="oui" className={fieldClass} data-id="element-3479" data-mp="380v81s9nvh16">
                    <option value="oui" data-id="element-3480" data-mp="3vqpf1t7aubdi">Oui</option>
                    <option value="non" data-id="element-3481" data-mp="3vs0itt0qvj4m">Non</option>
                  </select>
                </Field>
              </div>
            </fieldset>}

          {step === 1 && <fieldset data-id="element-3482" data-mp="3mxcfw9in6diz">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3483" data-mp="3mxvw9lsn8tuw">
                Section 2 — Entreprise et véhicule
              </legend>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3484" data-mp="ijxh2bg8tvrm">
                <Field id="entreprise" label="Nom de l’entreprise" required data-id="element-3485" data-mp="iiclqjm3u8sp">
                  <input id="entreprise" className={fieldClass} data-id="element-3486" data-mp="nc751lxsslr9" />
                </Field>
                <Field id="neq" label="Numéro d’entreprise (NEQ)" required hint="10 chiffres, sans espace" data-id="element-3487" data-mp="1jt1fyxooomyr">
                  <input id="neq" inputMode="numeric" className={fieldClass} data-id="element-3488" data-mp="1oqtj6nus3vi9" />
                </Field>
                <Field id="menu" label="Type de menu" required data-id="element-3489" data-mp="19u2f41a5tvmb">
                  <select id="menu" defaultValue={foodTruckMenuTypes[0]} className={fieldClass} data-id="element-3490" data-mp="1esdtmpbeguav">
                    {foodTruckMenuTypes.map(type => <option key={type} value={type} data-id="element-3491" data-mp="15sqoptjgp6d2">
                        {type}
                      </option>)}
                  </select>
                </Field>
                <Field id="mapaq" label="Numéro de permis du MAPAQ" required hint="Format MAPAQ-000000" data-id="element-3492" data-mp="r1zxamls89ah">
                  <input id="mapaq" className={fieldClass} data-id="element-3493" data-mp="w0kwqjt7moxv" />
                </Field>
                <Field id="plaque" label="Plaque d’immatriculation" required data-id="element-3494" data-mp="i594cj2j1ak4">
                  <input id="plaque" className={fieldClass} data-id="element-3495" data-mp="m487by4p6x8x" />
                </Field>
                <Field id="longueur" label="Longueur du véhicule (m)" required data-id="element-3496" data-mp="3hh8hts6o2qf">
                  <input id="longueur" type="number" step="0.1" className={fieldClass} data-id="element-3497" data-mp="3unikq5nvx2dn" />
                </Field>
                <Field id="cuisson" label="Mode de cuisson" required data-id="element-3498" data-mp="ci6wwcf46qu4">
                  <select id="cuisson" defaultValue="propane" className={fieldClass} data-id="element-3499" data-mp="7iarc8qrqrcg">
                    <option value="propane" data-id="element-3500" data-mp="mfbncofzt8gq">Propane</option>
                    <option value="electrique" data-id="element-3501" data-mp="mdqtl11i5tjx">Électrique</option>
                    <option value="mixte" data-id="element-3502" data-mp="hh0cqbhvitxg">Mixte</option>
                  </select>
                </Field>
                <Field id="generatrice" label="Génératrice" required hint="Le niveau sonore doit rester sous 65 dB" data-id="element-3503" data-mp="v7vel3tygarj">
                  <select id="generatrice" defaultValue="oui" className={fieldClass} data-id="element-3504" data-mp="r7bg9wxnb13t">
                    <option value="oui" data-id="element-3505" data-mp="156un8meq06ej">Oui</option>
                    <option value="non" data-id="element-3506" data-mp="153gjb9qerp4z">Non</option>
                  </select>
                </Field>
              </div>

              <Field id="description" label="Description de l’exploitation" required hint="Nombre d’employés, gestion des déchets, approvisionnement en eau." data-id="element-3507" data-mp="3tyy4wuctmbba">
                <textarea id="description" rows={4} className={`${fieldClass} resize-y`} data-id="element-3508" data-mp="6t70m0oh2c5k" />
              </Field>
            </fieldset>}

          {step === 2 && <fieldset data-id="element-3509" data-mp="liuy0qtj8qo0">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3510" data-mp="li3fohoy4pfo">
                Section 3 — Emplacements et horaire demandés
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3511" data-mp="ujkr3mres2y1">
                Seules les zones autorisées par le règlement ZON-2021-04 peuvent être demandées.
              </p>

              <fieldset className="mt-6" data-id="element-3512" data-mp="2rdo0qfjb34yc">
                <legend className={labelClass} data-id="element-3513" data-mp="2waebqog97acw">Zones demandées{requiredMark}</legend>
                <ul className="mt-2 space-y-2" data-id="element-3514" data-mp="2orexex7ng90e">
                  {foodTruckZones.map(zone => <li key={zone.id} data-id="element-3515" data-mp="2k0yh95cg29ac">
                      <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-3516" data-mp="2jzwu42i5lwc9">
                        <input type="checkbox" checked={zones.includes(zone.id)} onChange={() => toggle(zones, zone.id, setZones)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-3517" data-mp="2f251155rw46h" />
                        <span className="min-w-0" data-id="element-3518" data-mp="32rzdvmq3dsqq">
                          <span className="block text-sm font-bold text-ville-navy" data-id="element-3519" data-mp="2xrk1axdmwp9t">{zone.label}</span>
                          <span className="block text-[13px] text-slate-500" data-id="element-3520" data-mp="2tr1b17re3r9q">{zone.detail}</span>
                        </span>
                      </label>
                    </li>)}
                </ul>
              </fieldset>

              <fieldset className="mt-6" data-id="element-3521" data-mp="1ia1iqc51lcfp">
                <legend className={labelClass} data-id="element-3522" data-mp="368als50qqj33">Jours d’exploitation{requiredMark}</legend>
                <ul className="mt-2 flex flex-wrap gap-2" data-id="element-3523" data-mp="3ayr4o4nbywwu">
                  {foodTruckDays.map(day => {
                const active = days.includes(day);
                return <li key={day} data-id="element-3524" data-mp="318xrx0bgcbr0">
                        <button type="button" aria-pressed={active} onClick={() => toggle(days, day, setDays)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'bg-ville-navy text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo'}`} data-id="element-3525" data-mp="319zdhyq3ggmz">
                          {day}
                        </button>
                      </li>;
              })}
                </ul>
              </fieldset>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3526" data-mp="1w14xxpkg2kf6">
                <Field id="debut" label="Début de l’exploitation" required data-id="element-3527" data-mp="2azgt1rs65l48">
                  <input id="debut" type="date" defaultValue="2026-10-01" className={fieldClass} data-id="element-3528" data-mp="25zuhdmrzocxj" />
                </Field>
                <Field id="fin" label="Fin de l’exploitation" required data-id="element-3529" data-mp="1i9qfbjug8l8n">
                  <input id="fin" type="date" defaultValue="2027-05-31" className={fieldClass} data-id="element-3530" data-mp="1db6r1csduxd4" />
                </Field>
                <Field id="heure-debut" label="Heure d’ouverture" required data-id="element-3531" data-mp="1s0l16n3s9itk">
                  <input id="heure-debut" type="time" defaultValue="11:00" className={fieldClass} data-id="element-3532" data-mp="1s1w28f5ugcgz" />
                </Field>
                <Field id="heure-fin" label="Heure de fermeture" required hint="Maximum 22 h en zone résidentielle" data-id="element-3533" data-mp="zj8ndhhfxtoc">
                  <input id="heure-fin" type="time" defaultValue="20:00" className={fieldClass} data-id="element-3534" data-mp="ujmc3dl6ani6" />
                </Field>
              </div>
            </fieldset>}

          {step === 3 && <fieldset data-id="element-3535" data-mp="1o9d1dnkju21x">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3536" data-mp="1o8lf53cudgob">
                Section 4 — Documents et déclaration
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3537" data-mp="1y0hesiukteun">
                Formats acceptés : PDF, JPG ou PNG, maximum 10 Mo par fichier.
              </p>

              <ul className="mt-6 space-y-2" data-id="element-3538" data-mp="11cbgedo8ziot">
                {foodTruckDocuments.map(document => {
              const attached = documents.includes(document.id);
              return <li key={document.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-3539" data-mp="1aczsv3xztazl">
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${attached ? 'bg-ville-mist text-ville-deep' : 'bg-slate-200 text-slate-500'}`} aria-hidden="true" data-id="element-3540" data-mp="2ls6924qykde8">
                        <CheckIcon className="h-3 w-3" data-id="element-3541" data-mp="5bgr02hcfb8k" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-3542" data-mp="cnby5merc6a">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-3543" data-mp="c44jiudw66i">
                          {document.name}
                          {requiredMark}
                        </span>
                        <span className="block text-[13px] text-slate-500" data-id="element-3544" data-mp="3ngjw3n7b7n8z">{document.detail}</span>
                      </span>
                      <button type="button" onClick={() => toggle(documents, document.id, setDocuments)} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3545" data-mp="3ikcta3a7mrv9">
                        <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-3546" data-mp="bu4o60oyteks" />
                        {attached ? 'Remplacer' : 'Joindre'}
                      </button>
                    </li>;
            })}
              </ul>

              <label className="mt-6 flex items-start gap-3 rounded-2xl bg-ville-sand px-4 py-4" data-id="element-3547" data-mp="1v72r6mu6160">
                <input type="checkbox" checked={declared} onChange={event => setDeclared(event.target.checked)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-3548" data-mp="6vuslozbwbyn" />
                <span className="text-sm leading-relaxed text-slate-700" data-id="element-3549" data-mp="1n9bj67mlaljh">
                  Je déclare que les renseignements fournis sont exacts et m’engage à respecter le règlement sur
                  les commerces mobiles ainsi que les conditions du permis. Toute fausse déclaration peut
                  entraîner la révocation du permis.
                  {requiredMark}
                </span>
              </label>

              <p className="mt-4 text-[13px] text-slate-500" data-id="element-3550" data-mp="3ljaeg3ic3025">
                Aucun paiement n’est requis à l’envoi. Les frais de {foodTruckApplication.fee} vous seront
                réclamés uniquement si la Ville accepte la demande.
              </p>
            </fieldset>}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6" data-id="element-3551" data-mp="3qi3otn2bqay3">
            <button type="button" onClick={() => step === 0 ? navigate('/mes-permis') : setStep(current => current - 1 as Step)} className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3552" data-mp="8cgyabni0sew">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-3553" data-mp="1df8guobj8bix" />
              {step === 0 ? 'Annuler' : 'Étape précédente'}
            </button>

            {step < 3 ? <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3554" data-mp="1m83p7qwaqncx">
                Étape suivante
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3555" data-mp="13hlzzwaoa9wb" />
              </button> : <button type="submit" disabled={!declared} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" data-id="element-3556" data-mp="yi9crafw30t7">
                <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-3557" data-mp="28gqy80iktp46" />
                Transmettre ma demande
              </button>}
          </div>
        </form>
      </div>
    </main>;
}